/**
 * v138 — Aggregate contribution opt-in card.
 *
 * Renders in the Range Log settings panel. Only meaningful when the user
 * is signed in (v136.0). Anonymous users see a hint that signing in is
 * required to contribute. The opt-in defaults OFF and persists in D1.
 */

import { useEffect, useState } from 'react';
import { CloudCog, ShieldCheck, Loader2 } from 'lucide-react';
import { useIdentity } from '@/auth/useIdentity';
import {
  getOptInStatus,
  setOptIn,
  readLocalContribCount,
} from '@/sync/aggregateContribution';

export function AggregateContributionCard() {
  const { identity, loading: identityLoading } = useIdentity();
  const [contributing, setContributing] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [contribCount, setContribCount] = useState(0);

  useEffect(() => {
    if (!identity.signedIn) {
      setContributing(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const status = await getOptInStatus();
      if (cancelled) return;
      setContributing(status.contributing);
      setContribCount(readLocalContribCount());
    })();
    return () => {
      cancelled = true;
    };
  }, [identity.signedIn]);

  const handleToggle = async (on: boolean) => {
    setBusy(true);
    const ok = await setOptIn(on);
    setBusy(false);
    if (ok) setContributing(on);
  };

  if (identityLoading) {
    return (
      <div className="rounded-md border border-zinc-800 bg-zinc-950/40 p-4 flex items-center gap-2 text-xs text-zinc-500">
        <Loader2 className="w-3 h-3 animate-spin" /> Checking sign-in…
      </div>
    );
  }

  if (!identity.signedIn) {
    return (
      <div className="rounded-md border border-zinc-800 bg-zinc-950/40 p-4">
        <p className="text-[11px] font-mono uppercase tracking-[3px] text-cyan-300 mb-2">
          <CloudCog className="w-3.5 h-3.5 inline mr-1" />
          Contribute to the community dataset
        </p>
        <p className="text-sm text-zinc-300 mb-2">
          Sign in to opt in to the Methods-page community dataset.
        </p>
        <p className="text-[11px] text-zinc-500">
          Anonymous Range Log still works — you just can't contribute aggregate
          stats without an account.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950/40 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-[3px] text-cyan-300 mb-1">
            <CloudCog className="w-3.5 h-3.5 inline mr-1" />
            Contribute to the community dataset
          </p>
          <p className="text-sm text-zinc-200">
            Help build the Methods-page quarterly cut. Off by default.
          </p>
        </div>
        <label className="inline-flex items-center cursor-pointer">
          <span className="sr-only">Opt in to aggregate contribution</span>
          <input
            type="checkbox"
            className="sr-only peer"
            checked={!!contributing}
            disabled={busy}
            onChange={(e) => void handleToggle(e.target.checked)}
          />
          <span
            className={
              'w-11 h-6 rounded-full border transition-colors ' +
              (contributing
                ? 'bg-emerald-600/80 border-emerald-500'
                : 'bg-zinc-800 border-zinc-700')
            }
          >
            <span
              className={
                'block w-5 h-5 bg-zinc-100 rounded-full mt-0.5 transition-transform ' +
                (contributing ? 'translate-x-5 ml-0.5' : 'translate-x-0.5')
              }
            />
          </span>
        </label>
      </div>

      <p className="text-[12px] text-zinc-400 leading-relaxed mb-2">
        What we send when opted in: distance, shot count, mean radius, 95% group,
        coarse conditions (temp / wind / DA bucketed for k-anonymity), and a setup
        signature (FPS, weight, BC, FOC, spine, bow type).
      </p>
      <p className="text-[12px] text-zinc-400 leading-relaxed mb-2">
        What we never send: photos, location strings, custom labels, free-text
        notes, per-shot coordinates, your customer ID. The Worker stores rows
        keyed by a one-way HMAC so individual rows can't be re-linked to you
        even with database access.
      </p>
      <p className="text-[12px] text-zinc-400 leading-relaxed mb-3">
        Full contract on <a href="/pages/privacy" target="_top" className="text-cyan-400 hover:underline">/pages/privacy</a>.
        Toggle off any time — past contributions stay anonymized in the
        community dataset; future sessions stop sending immediately.
      </p>

      {contributing && (
        <div className="inline-flex items-center gap-1.5 px-2 py-1 border border-emerald-700/40 bg-emerald-950/30 rounded text-[11px] text-emerald-300">
          <ShieldCheck className="w-3 h-3" />
          Contributing — {contribCount} event{contribCount === 1 ? '' : 's'} shared
        </div>
      )}

      {busy && (
        <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-zinc-500">
          <Loader2 className="w-3 h-3 animate-spin" /> Updating…
        </div>
      )}
    </div>
  );
}
